import { Injectable } from '@angular/core';
import { ReportHtml } from 'tms-lib';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';

import { TmsWebBaseEditCreateService } from 'tms-web-shared';
import {
  Job,
  JobRequirement,
  JobStockAllocation,
  JobStockAvailable,
  JobStockIssue,
  Piece,
  PieceJob,
  YarnAnalysisRow,
} from '../../../models';

@Injectable({
  providedIn: 'root',
})
export class JobService extends TmsWebBaseEditCreateService<Job, Job> {
  constructor() {
    super('Job', $localize`Job`);
  }

  // Fired when something on one tab changes what the Details tab shows - adding or removing
  // a piece re-derives a warping job's Qty Required on the server, so the Details tab has to
  // re-read or it keeps showing the stale figure. The Details tab subscribes; the Pieces tab
  // fires it after an add/remove.
  public refreshDetails = new Subject<void>();

  getPieces(jobId: string): Observable<PieceJob[]> {
    return this.httpService.get<PieceJob[]>(`${this.apiUrl}/Pieces/${jobId}`);
  }

  // The jobs a piece has been through, in route order.
  getForPiece(pieceId: string): Observable<PieceJob[]> {
    return this.httpService.get<PieceJob[]>(`${this.apiUrl}/Piece/${pieceId}`);
  }

  // The pieces that could join this job. The eligibility rule is the API's - same
  // product, not cancelled, not already in stock, not already on the job.
  getAvailablePieces(jobId: string): Observable<Piece[]> {
    return this.httpService.get<Piece[]>(`${this.apiUrl}/AvailablePieces/${jobId}`);
  }

  addPiece(jobId: string, pieceId: string): Observable<any> {
    return this.httpService.post(`${this.apiUrl}/AddPiece/${jobId}/${pieceId}`, {});
  }

  removePiece(pieceJobId: string): Observable<any> {
    return this.httpService.delete(`${this.apiUrl}/RemovePiece/${pieceJobId}`);
  }

  getRequirements(jobId: string): Observable<JobRequirement[]> {
    return this.httpService.get<JobRequirement[]>(`${this.apiUrl}/Requirements/${jobId}`);
  }

  // Works out the yarn this job needs from the colourway's spec and stores it. Explicit
  // rather than automatic on save: it takes a snapshot of the spec, and the planner
  // chooses when to (re)take it.
  calculateYarnRequirements(jobId: string): Observable<any> {
    return this.httpService.post(`${this.apiUrl}/CalculateYarnRequirements/${jobId}`, {});
  }

  // Saving through here is what marks the row overridden, so the next recalculate leaves
  // the corrected figure alone.
  saveRequirement(model: JobRequirement): Observable<string> {
    return this.httpService.post<string>(`${this.apiUrl}/SaveRequirement`, model);
  }

  clearRequirementOverride(requirementId: string): Observable<any> {
    return this.httpService.delete(`${this.apiUrl}/Requirement/Override/${requirementId}`);
  }

  // --- yarn onto the job: allocate (reserve), then issue (hand to the floor) ---
  // Two separate steps on purpose: stock is often reserved weeks ahead and only issued
  // just before warping or weaving starts.

  getAvailableStock(jobRequirementId: string): Observable<JobStockAvailable[]> {
    return this.httpService.get<JobStockAvailable[]>(`${this.apiUrl}/AvailableStock/${jobRequirementId}`);
  }

  getAllocations(jobId: string): Observable<JobStockAllocation[]> {
    return this.httpService.get<JobStockAllocation[]>(`${this.apiUrl}/Allocations/${jobId}`);
  }

  // Replaces the requirement's allocations with what was picked, so the modal round-trips
  // rather than only ever adding.
  saveAllocations(jobRequirementId: string, allocations: Partial<JobStockAllocation>[]): Observable<any> {
    return this.httpService.post(`${this.apiUrl}/SaveAllocations/${jobRequirementId}`, allocations);
  }

  removeAllocation(allocationId: string): Observable<any> {
    return this.httpService.delete(`${this.apiUrl}/Allocation/${allocationId}`);
  }

  getIssues(jobId: string): Observable<JobStockIssue[]> {
    return this.httpService.get<JobStockIssue[]>(`${this.apiUrl}/Issues/${jobId}`);
  }

  issueAllocated(jobId: string): Observable<any> {
    return this.httpService.post(`${this.apiUrl}/IssueAllocated/${jobId}`, {});
  }

  removeIssue(issueId: string): Observable<any> {
    return this.httpService.delete(`${this.apiUrl}/Issue/${issueId}`);
  }

  // The weaving ticket, as HTML. `wrap` asks for the self-contained copy (its own
  // landscape styles) used for printing; the in-app preview takes it unwrapped because it
  // already sits inside a styled page.
  // ⚠️ The API returns a ReportHtml OBJECT, not a bare string. An endpoint returning a
  // raw string is served as text/plain, which httpService.get<T> cannot parse - the call
  // fails and the report renders blank with nothing in the console. Every HTML report in
  // the app goes through this shape.
  getWeavingTicket(jobId: string, wrap = false): Observable<string> {
    return this.httpService
      .get<ReportHtml>(`${this.apiUrl}/WeavingTicket/${jobId}?wrap=${wrap}`)
      .pipe(map((model: ReportHtml) => model.html));
  }

  weavingTicketPrinted(jobId: string): Observable<any> {
    return this.httpService.post(`${this.apiUrl}/WeavingTicketPrinted/${jobId}`, {});
  }

  // The traffic-light board's yarn analysis for the job's own requirements. A 400 carries the reason
  // there is nothing to show (no requirements calculated yet).
  yarnAnalysis(jobId: string): Observable<YarnAnalysisRow[]> {
    return this.httpService.get<YarnAnalysisRow[]>(`${this.apiUrl}/YarnAnalysis/${jobId}`);
  }

  // The job's live pieces in piece number order - its Piece Labels.
  pieceIds(jobId: string): Observable<string[]> {
    return this.httpService.get<string[]>(`${this.apiUrl}/PieceIds/${jobId}`);
  }
}
